import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { FiPlus } from "react-icons/fi";
import Swal from "sweetalert2";
import { Apis } from "../../../lib/Apis";

// Selective Excel marla sizes only
const SELECTIVE_MARLAS = [
  2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20, 32, 40, 60, 80,
];

const MARLA_OPTIONS = SELECTIVE_MARLAS.map((m) => {
  const n = Number(m);
  const isKanal = Number.isInteger(n) && n >= 20 && n % 20 === 0;
  const kanal = isKanal ? n / 20 : null;
  const kStr = kanal ? ` / ${kanal} Kanal` : "";
  return {
    key: String(m),
    marla: n,
    sqYards: n * 25,
    label: `${m} Marla${kStr}`,
  };
});

const HOUSE_TYPES = [
  {
    id: 1,
    storey: "Single storey",
    floors: "Ground and Mumty floor",
    label: "Single Storey — Ground + Mumty",
  },
  {
    id: 2,
    storey: "Double storey",
    floors: "Ground and First floor",
    label: "Double Storey — Ground + First + Mumty",
  },
  {
    id: 3,
    storey: "Double storey",
    floors: "Basement and Ground floor",
    label: "Double Storey — Basement + Ground + Mumty",
  },
  {
    id: 4,
    storey: "Triple storey",
    floors: "Ground, First and Second floor",
    label: "Triple Storey — Ground + First + Second + Mumty",
  },
  {
    id: 5,
    storey: "Triple storey",
    floors: "Basement, Ground and First floor",
    label: "Triple Storey — Basement + Ground + First + Mumty",
  },
  {
    id: 6,
    storey: "Four storey",
    floors: "Basement, Ground, First and Second floor",
    label: "Four Storey — Basement + Ground + First + Second + Mumty",
  },
];

const QUALITIES = [
  { key: "budget", label: "Budget", descLabel: "Budget" },
  { key: "aQuality", label: "A Quality", descLabel: "A" },
  { key: "premium", label: "Premium", descLabel: "Premium" },
];

/** Fill the fixed short-description template from current A/B selections. */
const buildShortDescription = (sideA, sideB) => {
  const htA = HOUSE_TYPES.find((h) => h.id === Number(sideA.htId)) || HOUSE_TYPES[0];
  const htB = HOUSE_TYPES.find((h) => h.id === Number(sideB.htId)) || HOUSE_TYPES[0];
  const qA =
    QUALITIES.find((q) => q.key === sideA.quality)?.descLabel || sideA.quality;
  const qB =
    QUALITIES.find((q) => q.key === sideB.quality)?.descLabel || sideB.quality;
  const marlaA = sideA.marla;
  const marlaB = sideB.marla;

  return (
    `Here we will give comparison of ${marlaA} marla, ${htA.storey} (${htA.floors}), ${qA} quality vs. ` +
    `${marlaB} marla, ${htB.storey} (${htB.floors}), ${qB} quality house in Pakistan. ` +
    `This only includes cost of constructions of ${marlaA} marla, ${htA.storey} (${htA.floors}) and ` +
    `${marlaB} marla, ${htB.storey} (${htB.floors}) and doesn’t include plot price.`
  );
};

const QUALITY_STYLE = {
  budget: { bg: "#dbeafe", color: "#1e40af" },
  aQuality: { bg: "#dcfce7", color: "#14532d" },
  premium: { bg: "#fef3c7", color: "#92400e" },
};

const formatPkr = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "—";
  const n = Math.round(Number(val));
  const sign = n < 0 ? "-" : "";
  return `${sign}PKR ${Math.abs(n).toLocaleString("en-PK")}`;
};

const formatPct = (val) => {
  if (val == null || Number.isNaN(Number(val)) || !Number.isFinite(Number(val))) {
    return "—";
  }
  const n = Number(val);
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
};

const pickTable = (tables, htId, quality) =>
  (tables || []).find(
    (t) => Number(t.htId) === Number(htId) && t.quality === quality,
  ) || null;

const isSideComplete = (side) =>
  Boolean(side?.marla && side?.htId && side?.quality);

const SideCard = ({ title, side, onChange, accent }) => {
  const qStyle = side.quality
    ? QUALITY_STYLE[side.quality] || QUALITY_STYLE.budget
    : null;
  return (
    <div
      className="rounded-2xl border bg-white p-4 shadow-sm"
      style={{ borderColor: accent }}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-extrabold text-[#15173d]">{title}</h3>
        {qStyle && (
          <span
            className="rounded-full px-2.5 py-0.5 text-[10px] font-bold"
            style={{ background: qStyle.bg, color: qStyle.color }}
          >
            {QUALITIES.find((q) => q.key === side.quality)?.label}
          </span>
        )}
      </div>

      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Marla
          </span>
          <select
            value={side.marla}
            onChange={(e) => onChange({ ...side, marla: e.target.value })}
            className="w-full rounded-xl border border-[#15173d]/10 bg-[#faf8fa] px-3 py-2 text-sm font-semibold text-[#15173d] outline-none focus:border-[#982598]"
          >
            <option value="">Select marla</option>
            {MARLA_OPTIONS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            House type
          </span>
          <select
            value={side.htId}
            onChange={(e) =>
              onChange({
                ...side,
                htId: e.target.value ? Number(e.target.value) : "",
              })
            }
            className="w-full rounded-xl border border-[#15173d]/10 bg-[#faf8fa] px-3 py-2 text-sm font-semibold text-[#15173d] outline-none focus:border-[#982598]"
          >
            <option value="">Select house type</option>
            {HOUSE_TYPES.map((ht) => (
              <option key={ht.id} value={ht.id}>
                {ht.id}. {ht.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Quality
          </span>
          <select
            value={side.quality}
            onChange={(e) => onChange({ ...side, quality: e.target.value })}
            className="w-full rounded-xl border border-[#15173d]/10 bg-[#faf8fa] px-3 py-2 text-sm font-semibold text-[#15173d] outline-none focus:border-[#982598]"
          >
            <option value="">Select quality</option>
            {QUALITIES.map((q) => (
              <option key={q.key} value={q.key}>
                {q.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
};

const ComparisonBlogs = () => {
  const navigate = useNavigate();
  const [sideA, setSideA] = useState({
    marla: "",
    htId: "",
    quality: "",
  });
  const [sideB, setSideB] = useState({
    marla: "",
    htId: "",
    quality: "",
  });
  const [mode, setMode] = useState("marla");
  const [loading, setLoading] = useState(false);
  const [tableA, setTableA] = useState(null);
  const [tableB, setTableB] = useState(null);
  const [displayA, setDisplayA] = useState("");
  const [displayB, setDisplayB] = useState("");

  const bothSelected = isSideComplete(sideA) && isSideComplete(sideB);

  const htA = useMemo(
    () => HOUSE_TYPES.find((h) => h.id === Number(sideA.htId)) || null,
    [sideA.htId],
  );
  const htB = useMemo(
    () => HOUSE_TYPES.find((h) => h.id === Number(sideB.htId)) || null,
    [sideB.htId],
  );
  const qA = QUALITIES.find((q) => q.key === sideA.quality)?.label || "";
  const qB = QUALITIES.find((q) => q.key === sideB.quality)?.label || "";

  const shortDescription = useMemo(() => {
    if (!bothSelected) return "";
    return buildShortDescription(sideA, sideB);
  }, [sideA, sideB, bothSelected]);

  const comparison = useMemo(() => {
    const greyA = Number(tableA?.totGrey) || 0;
    const finishA = Number(tableA?.totFinish) || 0;
    const totalA = Number(tableA?.grandTotal) || greyA + finishA;
    const greyB = Number(tableB?.totGrey) || 0;
    const finishB = Number(tableB?.totFinish) || 0;
    const totalB = Number(tableB?.grandTotal) || greyB + finishB;

    const dGrey = greyB - greyA;
    const dFinish = finishB - finishA;
    const dTotal = totalB - totalA;
    const pct = totalA > 0 ? (dTotal / totalA) * 100 : null;

    return {
      greyA,
      finishA,
      totalA,
      greyB,
      finishB,
      totalB,
      dGrey,
      dFinish,
      dTotal,
      pct,
    };
  }, [tableA, tableB]);

  const loadComparison = useCallback(async () => {
    if (!isSideComplete(sideA) || !isSideComplete(sideB)) {
      setTableA(null);
      setTableB(null);
      setDisplayA("");
      setDisplayB("");
      return;
    }

    setLoading(true);
    try {
      const marlaA = Number(sideA.marla);
      const marlaB = Number(sideB.marla);
      const sameMarla = marlaA === marlaB;

      const [resA, resB] = await Promise.all([
        Apis.getCostConstructionTables(marlaA, mode),
        sameMarla
          ? Promise.resolve(null)
          : Apis.getCostConstructionTables(marlaB, mode),
      ]);

      const payloadA = resA?.data?.data || resA?.data || {};
      const payloadB = sameMarla
        ? payloadA
        : resB?.data?.data || resB?.data || {};

      const pickedA = pickTable(payloadA.tables, sideA.htId, sideA.quality);
      const pickedB = pickTable(payloadB.tables, sideB.htId, sideB.quality);

      setTableA(pickedA);
      setTableB(pickedB);
      setDisplayA(payloadA.displayStr || `${marlaA} Marla`);
      setDisplayB(payloadB.displayStr || `${marlaB} Marla`);

      if (!pickedA || !pickedB) {
        Swal.fire({
          icon: "warning",
          title: "Incomplete data",
          text: "One or both sides have no matching cost table for this marla / house type / quality. Check Cost of Construction areas & rates.",
          confirmButtonColor: "#982598",
        });
      }
    } catch (err) {
      console.error("Comparison preview failed:", err);
      setTableA(null);
      setTableB(null);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load comparison tables.",
        confirmButtonColor: "#982598",
      });
    } finally {
      setLoading(false);
    }
  }, [sideA, sideB, mode]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  const labelA = bothSelected
    ? `${displayA || `${sideA.marla} Marla`} · ${htA?.label || "—"} · ${qA}`
    : "Select Side A";
  const labelB = bothSelected
    ? `${displayB || `${sideB.marla} Marla`} · ${htB?.label || "—"} · ${qB}`
    : "Select Side B";

  return (
    <div className="space-y-5 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-[#15173d]">
            Comparison Preview
          </h2>
          <p className="text-xs text-gray-500">
            Preview one A vs B construction-cost comparison (same rates/areas as
            Cost of Construction). Use Create to save a linked comparison blog.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-filter filter-btn"
            onClick={() => navigate("/comparison-blogs/list")}
          >
            Manage blogs
          </button>
          <button
            type="button"
            className="btn btn-primary h-[40px]"
            onClick={() => navigate("/comparison-blogs/create")}
          >
            <FiPlus className="text-white" />
            Create Comparison Blog
          </button>
          <button
            type="button"
            className="ui-btn-save"
            onClick={loadComparison}
            disabled={loading || !bothSelected}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-[#982598]/15 bg-[#f5eeff] px-4 py-3">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#982598"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginTop: 1, flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p className="text-[11px] font-medium text-[#982598]">
          Uses existing <strong>/cost-of-construction/tables/:marla</strong>.
          Selective Excel marlas only. One comparison (A vs B) per preview —
          same shape a Comparison Blog will use later.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
          Display mode
        </p>
        <button
          type="button"
          onClick={() => {
            if (mode === "marla") return;
            setMode("marla");
            setTableA(null);
            setTableB(null);
            setDisplayA("");
            setDisplayB("");
          }}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
            mode === "marla"
              ? "border-[#982598] bg-[#f5eeff] text-[#982598]"
              : "border-gray-200 bg-white text-gray-500"
          }`}
        >
          Marla
        </button>
        <button
          type="button"
          onClick={() => {
            if (mode === "sqYards") return;
            setMode("sqYards");
            setTableA(null);
            setTableB(null);
            setDisplayA("");
            setDisplayB("");
          }}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
            mode === "sqYards"
              ? "border-[#982598] bg-[#f5eeff] text-[#982598]"
              : "border-gray-200 bg-white text-gray-500"
          }`}
        >
          Sq Yards
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <SideCard
          title="House type 1 (Side A)"
          side={sideA}
          onChange={setSideA}
          accent="rgba(152,37,152,0.25)"
        />
        <SideCard
          title="House type 2 (Side B)"
          side={sideB}
          onChange={setSideB}
          accent="rgba(21,23,61,0.2)"
        />
      </div>

      <div
        style={{
          borderRadius: 18,
          background: "linear-gradient(135deg,#15173d 0%,#2d1050 100%)",
          padding: "18px 24px",
          boxShadow: "0 8px 28px rgba(21,23,61,0.18)",
        }}
      >
        <p
          style={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            color: "rgba(255,255,255,0.45)",
            marginBottom: 6,
          }}
        >
          Comparison preview
        </p>
        <p style={{ fontSize: 15, fontWeight: 800, color: "#fff", lineHeight: 1.45 }}>
          {labelA}
        </p>
        <p
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.45)",
            margin: "6px 0",
            fontWeight: 700,
          }}
        >
          vs
        </p>
        <p style={{ fontSize: 15, fontWeight: 800, color: "#f0c6ff", lineHeight: 1.45 }}>
          {labelB}
        </p>
      </div>

      <div className="rounded-2xl border border-[#15173d]/10 bg-white p-4 sm:p-5">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
          Short description
        </p>
        <p className="text-sm leading-relaxed text-[#374151]">
          {shortDescription ||
            "Select both sides (marla, house type, quality) to generate the short description."}
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#15173d]/10 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="bg-[#15173d] text-[11px] font-bold uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">House type</th>
                <th className="px-4 py-3 text-right">Grey structure Cost</th>
                <th className="px-4 py-3 text-right">Finishing Costs</th>
                <th className="px-4 py-3 text-right">Total Costs</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    Loading comparison…
                  </td>
                </tr>
              ) : !bothSelected ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-gray-400">
                    Select Side A and Side B to preview the comparison table.
                  </td>
                </tr>
              ) : (
                <>
                  <tr className="border-b border-[#f0e4f0] bg-[#faf8fa]">
                    <td className="px-4 py-3.5">
                      <p className="font-extrabold text-[#15173d]">House type 1</p>
                      <p className="mt-0.5 text-[11px] text-gray-500">{labelA}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.greyA)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.finishA)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#15173d]">
                      {formatPkr(comparison.totalA)}
                    </td>
                  </tr>
                  <tr className="border-b border-[#f0e4f0]">
                    <td className="px-4 py-3.5">
                      <p className="font-extrabold text-[#15173d]">House type 2</p>
                      <p className="mt-0.5 text-[11px] text-gray-500">{labelB}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.greyB)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums">
                      {formatPkr(comparison.finishB)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#15173d]">
                      {formatPkr(comparison.totalB)}
                    </td>
                  </tr>
                  <tr className="border-b border-[#f0e4f0] bg-[#f5eeff]/60">
                    <td className="px-4 py-3.5 font-extrabold text-[#982598]">
                      Difference
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dGrey)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-semibold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dFinish)}
                    </td>
                    <td className="px-4 py-3.5 text-right font-extrabold tabular-nums text-[#982598]">
                      {formatPkr(comparison.dTotal)}
                    </td>
                  </tr>
                  <tr className="bg-[#15173d]/[0.03]">
                    <td className="px-4 py-3.5 font-extrabold text-[#15173d]">
                      Incremental Percentage cost
                    </td>
                    <td colSpan={2} className="px-4 py-3.5 text-right text-xs text-gray-400">
                      (Total B − Total A) / Total A
                    </td>
                    <td className="px-4 py-3.5 text-right text-lg font-black tabular-nums text-[#15173d]">
                      {formatPct(comparison.pct)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ComparisonBlogs;
